/* eslint-disable no-confusing-arrow */
import React, { useState, useCallback, useRef, lazy, Suspense } from 'react';

// Redux
import { useDispatch } from 'react-redux';

// Material
import Grid from '@material-ui/core/Grid';
import Tooltip from '@material-ui/core/Tooltip';
import IconButton from '@material-ui/core/IconButton';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Box from '@material-ui/core/Box';

// ReactCSV
import { CSVLink } from 'react-csv';

// Helpers
import { formatDateAndTime } from 'helpers/date';

// Actions
import * as ReportActions from 'store/ducks/reports/actions';
import { setOpen, setClose } from 'store/ducks/dialogs/actions';
import * as FleetActions from 'store/ducks/fleet/actions';

// Constants
import { REPORTS_TYPES } from 'constants/report';
import ITEMS from 'constants/itemsByFilter';
import TITLES from 'constants/titles';
import TITLES_OPTIONS from 'constants/infoModulesTitle';

// Styles
import useStyles from './CarQuantityReportDetails.styles';

// Lazy Components
const Filter = lazy(() => import('components/Filter'));
const MainContent = lazy(() =>
  import('components').then((mod) => ({ default: mod.MainContent })),
);
const ReportTitle = lazy(() =>
  import('components').then((mod) => ({ default: mod.ReportTitle })),
);
const SearchComponent = lazy(() =>
  import('components').then((mod) => ({ default: mod.SearchComponent })),
);
const TablePlaceholder = lazy(() =>
  import('components/TablePlaceholder').then((mod) => ({
    default: mod.TablePlaceholder,
  })),
);
const TabPanel = lazy(() =>
  import('components/TabPanel').then((mod) => ({ default: mod.TabPanel })),
);
const TableCarQuantity = lazy(() =>
  import('./components/TableCarQuantity').then((mod) => ({
    default: mod.TableCarQuantity,
  })),
);
const Submissions = lazy(() =>
  import('../../../../components').then((mod) => ({
    default: mod.Submissions,
  })),
);
const History = lazy(() =>
  import('../../../../components').then((mod) => ({ default: mod.History })),
);

interface CarDemandReportDatailsProps {
  setSubmissionData: (param: any) => void;
  setIsAddSubmissiion: (param: any) => void;
  setIsEditSubmissiion: (param: any) => void;
}

const CarQuantityReportDetails: React.FC<CarDemandReportDatailsProps> = ({
  setSubmissionData,
  setIsAddSubmissiion,
  setIsEditSubmissiion,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const csvRef = useRef(null);

  const [generalNavGuide, setGeneralNavGuide] = useState(0);
  const [reportIsVisible, setReportIsVisible] = useState(false);
  const [tableCSV, setTableCSV] = useState('');
  const [periodString, setPeriodString] = useState('');

  const handleChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setGeneralNavGuide(newValue);
  };

  const handleLoadData = useCallback(() => {
    setReportIsVisible(true);

    // initial date
    const MS_DAY = 86400000;
    const now = new Date();
    const yest = new Date(now.getTime() - MS_DAY);

    dispatch(
      FleetActions.listCarsReportQuantityRequest({
        startDate: new Date().setHours(0, 0, 0),
        endDate: new Date().setHours(23, 59, 59),
      }),
    );

    dispatch(ReportActions.setSelectedStartDate({ selectedStartDate: yest }));
    dispatch(ReportActions.setSelectedEndDate({ selectedEndDate: now }));

    const tempPeriodString = `Dia: ${new Date().toLocaleDateString('pt-BR', {
      dateStyle: 'short',
    })}`;

    setPeriodString(tempPeriodString);
  }, [dispatch]);

  const handleApply = useCallback(
    ({ startedAt, startDate, endDate, selectedLines, selectedCars }) => {
      dispatch(setClose({}));
      setReportIsVisible(true);

      const isPeriod = startDate?.getTime() < endDate?.getTime();
      let startDateValue;
      let endDateValue;
      if (isPeriod) {
        startDateValue = new Date(startDate).getTime();
        endDateValue = new Date(endDate).getTime();
      } else if (startedAt) {
        startDateValue = new Date(startedAt).setHours(0, 0, 0);
        endDateValue = new Date(startedAt).setHours(23, 59, 59);
      }
      dispatch(
        FleetActions.listCarsReportQuantityRequest({
          startDate: startDateValue,
          endDate: endDateValue,
          lineId:
            selectedLines?.length > 0
              ? selectedLines.map((e: any) => e.id).join()
              : undefined,
          carId:
            selectedCars?.length > 0
              ? selectedCars.map((e: any) => e.id).join()
              : undefined,
        }),
      );

      const tempPeriodString = `De: ${
        isPeriod
          ? formatDateAndTime(new Date(startDate))
          : formatDateAndTime(new Date(startedAt))
      } até: ${
        isPeriod
          ? formatDateAndTime(new Date(endDate))
          : formatDateAndTime(new Date(startedAt))
      }`;

      let updatedPeriodString = tempPeriodString;

      if (selectedLines?.length > 0) {
        const tempLinesString = `Linhas: ${selectedLines
          .map((e: any) => e.name)
          .join(', ')}`;
        updatedPeriodString += ', ' + tempLinesString;
      }

      if (selectedCars?.length > 0) {
        const tempLinesString = `${TITLES.carTitle}: ${selectedCars
          .map((e: any) => e.name)
          .join(', ')}`;
        updatedPeriodString += ', ' + tempLinesString;
      }

      setPeriodString(updatedPeriodString);

      if (!reportIsVisible) setReportIsVisible(true);
    },
    [reportIsVisible],
  );
  return (
    <div className={classes.root}>
      <MainContent className={classes.paperRoot}>
        <Grid
          container
          justifyContent='space-between'
          className={classes.gridContainer}>
          <Grid item>
            <Suspense fallback={'Carregando...'}>
              <ReportTitle
                titleVariant='h4'
                title='Relatórios de Operação • Quantidade por Carro'
                tooltipTitle={
                  TITLES_OPTIONS.REPORTS.OPERATION.DEMAND_PER_CAR.VALUE
                }
              />
            </Suspense>
          </Grid>
          {reportIsVisible && (
            <Grid item className={classes.icons}>
              <Grid item>
                <CSVLink
                  filename='Relatório_de_quantidade_por_carro.csv'
                  data={tableCSV}
                  ref={csvRef}>
                  <Tooltip title='Baixar CSV' placement='top'>
                    <IconButton />
                  </Tooltip>
                </CSVLink>
              </Grid>
            </Grid>
          )}
        </Grid>
        <Grid container className={classes.chipFilter}>
          <Box display='flex' alignItems='center' width='100%' marginTop='8px'>
            <Suspense fallback={'Carregando...'}>
              <SearchComponent
                disabledInput={!reportIsVisible}
                modalFilter={() => {
                  dispatch(
                    setOpen({
                      open: true,
                      content: (
                        <Suspense fallback={'Carregando...'}>
                          <Filter
                            fields={ITEMS.carDemand}
                            handleApply={handleApply}
                          />
                        </Suspense>
                      ),
                      maxWidth: 'xl',
                    }),
                  );
                }}
              />
            </Suspense>
            {/* <Grid container justifyContent='flex-end'>
              <Button
                className={classes.addButton}
                onClick={() => setIsAddSubmissiion(true)}>
                <Typography style={{ marginRight: 8, fontSize: 20 }}>
                  +
                </Typography>{' '}
                <Typography style={{ fontSize: 12 }}>Programar Novo</Typography>
              </Button>
            </Grid> */}
          </Box>
        </Grid>
        <Tabs
          indicatorColor='primary'
          onChange={handleChange}
          style={{
            background: '#ffffff',
            margin: '10px 0px 32px 16px',
          }}
          textColor='primary'
          value={generalNavGuide}>
          <Tab label='Relatório' />
          <Tab label='Envios' />
          <Tab label='Histórico' />
        </Tabs>
        <Suspense fallback={'Carregando...'}>
          <TabPanel index={0} value={generalNavGuide}>
            {!reportIsVisible && (
              <Suspense fallback={'Carregando...'}>
                <TablePlaceholder onClick={handleLoadData} />
              </Suspense>
            )}
            {reportIsVisible && (
              <Suspense fallback={'Carregando...'}>
                <TableCarQuantity
                  setTableCSV={setTableCSV}
                  periodString={periodString}
                  csvRef={csvRef}
                />
              </Suspense>
            )}
          </TabPanel>
          <TabPanel index={1} value={generalNavGuide}>
            <Suspense fallback={'Carregando...'}>
              <Submissions
                setSubmissionData={setSubmissionData}
                setIsEditSubmissiion={setIsEditSubmissiion}
                reportType={REPORTS_TYPES.CAR_QUANTITY}
              />
            </Suspense>
          </TabPanel>{' '}
          <TabPanel index={2} value={generalNavGuide}>
            <Suspense fallback={'Carregando...'}>
              <History reportType={REPORTS_TYPES.CAR_QUANTITY} />
            </Suspense>
          </TabPanel>
        </Suspense>
      </MainContent>
    </div>
  );
};

export default CarQuantityReportDetails;
export { CarQuantityReportDetails };
